import React, { useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import LockFilledIcon from '@atlaskit/icon/core/migration/lock-locked--lock-filled';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsPanelShown } from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import messages from './messages.tsx';

export const Header = ({ onClose }: { onClose: () => void }) => {
	const { formatMessage } = useIntl();
	const closeButtonRef = useRef<HTMLElement>(null);
	const [isPanelShown] = useIsPanelShown();

	useEffect(() => {
		if (!fg('jfp_a11y_issue_comments_summary_panel_focus')) {
			return;
		}
		if (isPanelShown) {
			closeButtonRef.current?.focus();
		}
	}, [isPanelShown]);

	return (
		<Container>
			<TitleWrapper>
				<LockFilledIcon label="" LEGACY_size="small" color={token('color.icon.subtle')} />
				<Heading size="xxsmall" as="h2">
					{formatMessage(messages.onlyVisibleToYou)}
				</Heading>
			</TitleWrapper>
			<Button
				appearance="subtle"
				spacing="compact"
				iconBefore={
					<CrossIcon
						label={formatMessage(messages.close)}
						LEGACY_size="small"
						color="currentColor"
					/>
				}
				onClick={(event, analyticsEvent) => {
					onClose();
					fireUIAnalytics(analyticsEvent, 'close');
				}}
				testId="issue-smart-request-summary.ui.header.close-button"
				{...(fg('jfp_a11y_issue_comments_summary_panel_focus') ? { ref: closeButtonRef } : {})}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TitleWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingBlock: token('space.050'),
	paddingInline: token('space.150'),
	borderBottomWidth: 1,
	borderBottomStyle: 'solid',
	borderBottomColor: token('color.border'),
});
